import WaveformPlayer from 'components/WaveformPlayer/WaveformPlayer'
import { useContext, useEffect, useRef, useState } from 'react'
import Tabs from 'components/Tabs/Tabs'
import MetadataTab from 'views/TrackView/tabs/MetadataTab'
import DesignTab from 'views/TrackView/tabs/DesignTab'
import { useTracksQuery, useWaveformQuery } from 'api/api'
import SettingsTab from './tabs/SettingsTab'
import particleSystems from '../../config/particleSystems'
import LoadingBlocker from '../../components/LoadingSpinner/LoadingBlocker'
import { ReactComponent as PrevIcon } from 'assets/icons/chev-prev.svg'
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg'
import WaveformCanvasExample from '../../components/WaveformPlayer/WaveformCanvasExample'
import { ReactComponent as MusicIcon } from '../../assets/icons/music.svg'
import { ReactComponent as UploadIcon } from 'assets/icons/upload.svg'
import { useDispatch } from 'react-redux'
import { UploadContext } from '../../contexts/UploadContext'
import ProgressBar from '../../components/ProgressBar/ProgressBar'
import { current } from '@reduxjs/toolkit'
import { FileUploader } from 'react-drag-drop-files'

const emptyValues = {
  name: '',
  album: '',
  albumArtist: '',
  composer: '',
  genre: '',
  artist: '',
}
const TrackView = ({ track, onSubmit, onCancel, isLoading }) => {
  const { data: tracksData } = useTracksQuery()
  const currentTrack =
    tracksData?.data?.find((find) => find.id === track.id) || {}
  const { fileConversionResult, originalFilename, waveform } = currentTrack

  const { data: waveformData, isFetching: waveformDataLoading } =
    useWaveformQuery(waveform)

  const dispatch = useDispatch()
  const uploadContext = useContext(UploadContext)
  const replaceTrackRef = useRef(null)
  const [selectedTab, setSelectedTab] = useState('metadata')
  const [values, setValues] = useState({
    ...track,
  })
  const [replacementFile, setReplacementFile] = useState(null)
  const [isDroppingFile, setIsDroppingFile] = useState(false)

  useEffect(() => {
    setValues({ ...emptyValues, ...track })
  }, [track.id])

  useEffect(() => {
    function handleEscapeKey(evt) {
      const { code } = evt
      if (code === 'Escape') {
        onCancel(evt)
      }
    }

    document.addEventListener('keydown', handleEscapeKey)
    return () => document.removeEventListener('keydown', handleEscapeKey)
  }, [])

  useEffect(() => {
    const interval = setInterval(() => {
      setReplacementFile(
        uploadContext?.files?.[track.id]?.status === 'uploading'
          ? uploadContext?.files?.[track.id]
          : null,
      )
    }, 100)
    return () => clearInterval(interval)
  }, [])

  const { name } = track
  const theme = values.data.theme

  const tabs = [
    {
      id: 'metadata',
      name: 'Metadata',
    },
    {
      id: 'design',
      name: 'Design',
    },
    {
      id: 'settings',
      name: 'Settings',
    },
  ]

  const particleSystemData = particleSystems.find(
    (system) => system.id === theme?.particleSystem?.type,
  )?.data

  function handleReplaceAudio(evt) {
    const file = evt.target.files?.[0]
    uploadContext.replaceTrack({ track, dispatch, file })
    evt.target.value = null
  }

  function handleCancelReplaceUpload() {
    uploadContext.cancelUpload(replacementFile)
  }

  function handleFileUpload(files) {
    if (replacementFile) {
      handleCancelReplaceUpload()
    }
    const file = files?.[0]
    uploadContext.replaceTrack({ track, dispatch, file })
    setIsDroppingFile(false)
  }

  function handleDragOver(evt) {
    if (evt?.dataTransfer?.types?.[0] === 'Files') {
      setIsDroppingFile(true)
    }
  }

  return (
    <div
      className="h-full flex flex-col bg-gray relative"
      onDragOver={handleDragOver}
      onDragEnd={() => setIsDroppingFile(false)}
      onDrop={() => setIsDroppingFile(false)}
    >
      <div className="">
        <div className="px-6 py-4 bg-gray-light">
          <button
            onClick={onCancel}
            className="flex items-center justify-between mb-2"
          >
            <PrevIcon fill="#ccc" />{' '}
            <span className="block ml-2 text-sm">Back</span>
          </button>
          <h2>{name}</h2>
          <div className="flex items-center">
            {replacementFile || fileConversionResult === 'converting' ? (
              <div className="mt-2 mb-[-2px] flex">
                <ProgressBar percent={replacementFile?.progress}>
                  {replacementFile ? (
                    <div className="w-full text-center text-sm">{`${replacementFile?.progress}%`}</div>
                  ) : (
                    <div className="processing">
                      <div>Processing File</div>
                    </div>
                  )}
                </ProgressBar>
                {replacementFile ? (
                  <div>
                    <button
                      onClick={handleCancelReplaceUpload}
                      data-tooltip="Cancel Upload"
                      className="bg-white-purple hover:bg-white dark:bg-gray-light dark:hover:bg-gray-dark ml-2 w-[20px] h-[20px] flex items-center  rounded-full"
                    >
                      <CloseIcon fill="#CCC" height="16" />
                    </button>
                  </div>
                ) : null}
              </div>
            ) : (
              <>
                <div className="flex items-center text-xs font-bold text-white-dark pt-[2px]">
                  {originalFilename}
                </div>
                <button
                  className="bg-gray-light b-0 border-0 h-[24px] w-[24px] ml-1 flex items-center justify-center rounded"
                  data-tooltip="Replace Audio"
                  onClick={() => replaceTrackRef.current.click()}
                >
                  <UploadIcon height="16" width="16" fill="#ccc" />
                </button>
              </>
            )}
          </div>
        </div>
        <div className="pt-12 pb-8 px-6">
          <WaveformPlayer
            key={`${track.id}-${waveform}`}
            track={{
              ...values,
              duration: currentTrack.duration,
              fileConversionResult,
              theme: theme,
            }}
            waveformData={waveformData}
            particleSystemData={particleSystemData}
          />
        </div>
      </div>
      <div className="mt-2">
        <Tabs
          tabs={tabs}
          selectedTab={selectedTab}
          onClick={(tab) => setSelectedTab(tab.id)}
        />
      </div>
      <div className="p-6 flex-1 overflow-y-auto">
        {selectedTab === 'metadata' && (
          <MetadataTab
            values={values}
            onChange={setValues}
            onSubmit={() => onSubmit(values)}
          />
        )}
        {selectedTab === 'design' && (
          <DesignTab
            values={values}
            onChange={setValues}
            onSubmit={() => onSubmit(values)}
          />
        )}
        {selectedTab === 'settings' && (
          <SettingsTab
            values={values}
            onChange={setValues}
            onSubmit={() => onSubmit(values)}
          />
        )}
      </div>

      <div className="absolute top-4 right-4 flex items-center">
        {/*
        <button className="bg-blue text-white rounded text-sm font-bold transition tracking-wider hover:bg-blue-med px-6 py-2">
          Share
        </button>
        */}

        <button
          className="button icon bg-transparent  text-white-dark ml-4"
          onClick={onCancel}
        >
          <CloseIcon fill="#ccc" height="26" />
        </button>
      </div>

      <input
        className="hidden"
        type="file"
        accept=".flac,audio/*"
        onChange={handleReplaceAudio}
        ref={replaceTrackRef}
      />

      <FileUploader
        multiple={true}
        disabled={true}
        classes={`file-uploader ${isDroppingFile ? 'dropping-file' : ''}`}
        dropMessageStyle={{ display: 'none' }}
        handleChange={handleFileUpload}
        onDraggingStateChange={(dragging) => setIsDroppingFile(dragging)}
        name="file"
      >
        <div>
          <div className="flex justify-center items-center">
            <MusicIcon fill="#d63b5c" height="100" width="100" />
          </div>
          <h3 className="text-pink text-2xl">
            Drop file to upload replacement track.
          </h3>
        </div>
      </FileUploader>

      {isLoading && <LoadingBlocker />}
    </div>
  )
}

export default TrackView
